import { Provider } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, map, of } from 'rxjs';
import {
  BREADCRUMB_LABEL_RESOLVERS,
  BREADCRUMB_SIBLINGS_LOADERS,
  BreadcrumbLabelResolver,
  BreadcrumbSiblingsLoader,
  BreadcrumbItem,
  BreadcrumbResolvedItem,
  fetchAllPages,
  isNavSection,
} from '@zag/ui';
import { PermissionService } from '@zag/auth/ui';
import { FeatureAccessClientService } from '@zag/features/ui';
import { NAV_CONFIG } from './navigation.config';
import { type Calendar, type MyCalendarsResponse } from '@zag/calendar/models';

/**
 * API response types for breadcrumb data fetching.
 */
interface PersonListItem {
  id: string;
  firstName: string;
  lastName: string;
  profilePicture?: string | null;
}

interface NamedListItem {
  id: string;
  name: string;
}

interface UserListItem extends NamedListItem {
  image?: string | null;
}

interface AppearanceItem extends NamedListItem {
  icon?: string | null;
  color?: string | null;
  imageUrl?: string | null;
}

/**
 * Helper to extract data from API response.
 * Some endpoints return {data: T}, others return T directly.
 *
 * We detect a wrapper by checking if 'data' is present AND the object
 * has only wrapper-like keys (data, meta, pagination, etc.)
 */
function extractData<T>(response: T | { data: T } | undefined): T | undefined {
  if (!response) return undefined;
  if (typeof response === 'object' && 'data' in response) {
    const keys = Object.keys(response);
    // If the only keys are wrapper keys, extract .data
    const wrapperKeys = new Set(['data', 'meta', 'pagination', 'total', 'page', 'limit']);
    const isWrapper = keys.every(key => wrapperKeys.has(key));
    if (isWrapper) {
      return (response as { data: T }).data;
    }
  }
  return response as T;
}

/**
 * Factory for creating label resolvers.
 * These resolve dynamic labels (e.g., person ID -> person name).
 */
function createLabelResolvers(http: HttpClient): Record<string, BreadcrumbLabelResolver> {
  return {
    person: async (params: Record<string, string>): Promise<BreadcrumbResolvedItem | string> => {
      try {
        const response = await firstValueFrom(http.get<PersonListItem | { data: PersonListItem }>(`/api/v1/core/persons/${params['id']}`));
        const person = extractData(response);
        if (!person) return params['id'] || 'Unknown';
        return { label: `${person.firstName} ${person.lastName}`, icon: 'person', color: null, imageUrl: person.profilePicture ?? null };
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    team: async (params: Record<string, string>): Promise<BreadcrumbResolvedItem | string> => {
      try {
        const response = await firstValueFrom(
          http.get<AppearanceItem | { data: AppearanceItem }>(`/api/v1/planning/teams/${params['id']}`)
        );
        const team = extractData(response);
        if (!team || !team.name) return params['id'] || 'Unknown';
        return { label: team.name, icon: team.icon ?? 'groups', color: team.color ?? null, imageUrl: team.imageUrl ?? null };
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    department: async (params: Record<string, string>): Promise<BreadcrumbResolvedItem | string> => {
      try {
        const response = await firstValueFrom(
          http.get<AppearanceItem | { data: AppearanceItem }>(`/api/v1/core/departments/${params['id']}`)
        );
        const department = extractData(response);
        if (!department || !department.name) return params['id'] || 'Unknown';
        return {
          label: department.name,
          icon: department.icon ?? 'apartment',
          color: department.color ?? null,
          imageUrl: department.imageUrl ?? null,
        };
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    skill: async (params: Record<string, string>): Promise<BreadcrumbResolvedItem | string> => {
      try {
        const response = await firstValueFrom(http.get<AppearanceItem | { data: AppearanceItem }>(`/api/v1/core/skills/${params['id']}`));
        const skill = extractData(response);
        if (!skill || !skill.name) return params['id'] || 'Unknown';
        return { label: skill.name, icon: skill.icon ?? 'psychology', color: skill.color ?? null, imageUrl: skill.imageUrl ?? null };
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    shift: async (params: Record<string, string>): Promise<BreadcrumbResolvedItem | string> => {
      try {
        const response = await firstValueFrom(
          http.get<AppearanceItem | { data: AppearanceItem }>(`/api/v1/planning/shifts/${params['id']}`)
        );
        const shift = extractData(response);
        if (!shift || !shift.name) return params['id'] || 'Unknown';
        return { label: shift.name, icon: shift.icon ?? 'schedule', color: shift.color ?? null, imageUrl: shift.imageUrl ?? null };
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    plan: async (params: Record<string, string>) => {
      try {
        const response = await firstValueFrom(http.get<NamedListItem | { data: NamedListItem }>(`/api/v1/planning/plans/${params['id']}`));
        const plan = extractData(response);
        return { label: plan?.name || params['id'] || 'Unknown', icon: 'assignment' };
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    strategy: async (params: Record<string, string>): Promise<BreadcrumbResolvedItem | string> => {
      try {
        const response = await firstValueFrom(
          http.get<AppearanceItem | { data: AppearanceItem }>(`/api/v1/planning/strategies/${params['id']}`)
        );
        const strategy = extractData(response);
        if (!strategy || !strategy.name) return params['id'] || 'Unknown';
        return {
          label: strategy.name,
          icon: strategy.icon ?? 'lightbulb',
          color: strategy.color ?? null,
          imageUrl: strategy.imageUrl ?? null,
        };
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    shiftUnit: async (params: Record<string, string>) => {
      try {
        const unitId = params['unitId'] || params['id'];
        if (!unitId) return 'Unknown';
        const response = await firstValueFrom(
          http.get<{ shift: NamedListItem } | { data: { shift: NamedListItem } }>(`/api/v1/planning/shift-units/${unitId}`)
        );
        const unit = extractData(response);
        return unit?.shift?.name || unitId;
      } catch {
        return params['unitId'] || params['id'] || 'Unknown';
      }
    },

    role: async (params: Record<string, string>) => {
      // Roles use their ID as the display name (e.g., "admin", "manager")
      return { label: params['id'] || 'Unknown', icon: 'security' };
    },

    group: async (params: Record<string, string>) => {
      try {
        const response = await firstValueFrom(http.get<NamedListItem | { data: NamedListItem }>(`/api/v1/rbac/groups/${params['id']}`));
        const group = extractData(response);
        return { label: group?.name || params['id'] || 'Unknown', icon: 'groups' };
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    user: async (params: Record<string, string>) => {
      try {
        const response = await firstValueFrom(http.get<UserListItem | { data: UserListItem }>(`/api/v1/rbac/users/${params['id']}`));
        const user = extractData(response);
        return { label: user?.name || params['id'] || 'Unknown', icon: 'person', imageUrl: user?.image ?? null };
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    location: async (params: Record<string, string>): Promise<BreadcrumbResolvedItem | string> => {
      try {
        const response = await firstValueFrom(
          http.get<AppearanceItem | { data: AppearanceItem }>(`/api/v1/core/locations/${params['id']}`)
        );
        const location = extractData(response);
        if (!location || !location.name) return params['id'] || 'Unknown';
        return {
          label: location.name,
          icon: location.icon ?? 'location_on',
          color: location.color ?? null,
          imageUrl: location.imageUrl ?? null,
        };
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    notification: async (params: Record<string, string>) => {
      try {
        const response = await firstValueFrom(
          http.get<{ id: string; title: string } | { data: { id: string; title: string } }>(`/api/v1/notifications/${params['id']}`)
        );
        const notification = extractData(response);
        return { label: notification?.title || params['id'] || 'Unknown', icon: 'notifications' };
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    webhook: async (params: Record<string, string>) => {
      try {
        const response = await firstValueFrom(
          http.get<NamedListItem | { data: NamedListItem }>(`/api/v1/notifications/webhooks/${params['id']}`)
        );
        const webhook = extractData(response);
        return { label: webhook?.name || params['id'] || 'Unknown', icon: 'webhook' };
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    terminalGroup: async (params: Record<string, string>) => {
      try {
        const { data } = await firstValueFrom(http.get<{ data: NamedListItem[] }>('/api/v1/terminals/groups'));
        return { label: data.find(group => group.id === params['id'])?.name || params['id'] || 'Unknown', icon: 'workspaces' };
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    calendar: async (params: Record<string, string>) => {
      try {
        const response = await firstValueFrom(
          http.get<NamedListItem | { data: NamedListItem }>(`/api/v1/calendar/calendars/${params['id']}`)
        );
        const calendar = extractData(response);
        return {
          label: calendar?.name || params['id'] || 'Unknown',
          icon: 'calendar_month',
        };
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    customPropertyDefinition: async (params: Record<string, string>) => {
      try {
        const response = await firstValueFrom(
          http.get<{ label: string } | { data: { label: string } }>(`/api/v1/core/custom-property-definitions/${params['id']}`)
        );
        const def = extractData(response);
        return def?.label || params['id'] || 'Unknown';
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    slotTypeDefinition: async (params: Record<string, string>) => {
      try {
        const response = await firstValueFrom(
          http.get<{ label: string } | { data: { label: string } }>(`/api/v1/calendar/slot-type-definitions/${params['id']}`)
        );
        const def = extractData(response);
        return def?.label || params['id'] || 'Unknown';
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    balanceTypeDefinition: async (params: Record<string, string>) => {
      try {
        const response = await firstValueFrom(
          http.get<{ label: string } | { data: { label: string } }>(`/api/v1/time-tracking/balance-type-definitions/${params['id']}`)
        );
        const def = extractData(response);
        return def?.label || params['id'] || 'Unknown';
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    documentType: async (params: Record<string, string>) => {
      try {
        const response = await firstValueFrom(
          http.get<{ label: string } | { data: { label: string } }>(`/api/v1/documents/document-types/${params['id']}`)
        );
        const def = extractData(response);
        return def?.label || params['id'] || 'Unknown';
      } catch {
        return params['id'] || 'Unknown';
      }
    },
  };
}

function normalizeUrl(url: string): string {
  return url.split('?')[0].replace(/^\/+/, '').replace(/\/+$/, '');
}

/**
 * Maps API items to breadcrumb sibling items, preserving source order and including
 * the current item marked with isCurrent: true. Uses exact route comparison so IDs
 * that share a prefix cannot be misclassified as the current entry.
 */
function toSiblingItems<T extends { id: string }>(items: T[], currentUrl: string, mapItem: (item: T) => BreadcrumbItem): BreadcrumbItem[] {
  const normalizedCurrent = normalizeUrl(currentUrl);
  return items.map(item => {
    const breadcrumbItem = mapItem(item);
    const itemPath = normalizeUrl((breadcrumbItem.route ?? []).filter(Boolean).join('/'));
    return itemPath === normalizedCurrent ? { ...breadcrumbItem, isCurrent: true } : breadcrumbItem;
  });
}

/**
 * Factory for creating siblings loaders.
 * These load sibling items for dropdown navigation.
 */
function createSiblingsLoaders(
  http: HttpClient,
  permissionService: PermissionService,
  featureAccess: FeatureAccessClientService
): Record<string, BreadcrumbSiblingsLoader> {
  return {
    // Entity-based siblings (loaded from API)
    persons: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<PersonListItem>(http, { basePath: '/api/v1/core/persons', sort: 'lastName:asc' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, person => ({
            label: `${person.firstName} ${person.lastName}`,
            route: ['/persons', person.id, 'edit'],
            icon: 'person',
            color: null,
            imageUrl: person.profilePicture ?? null,
          }))
        )
      ),

    teams: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<AppearanceItem>(http, { basePath: '/api/v1/planning/teams', sort: 'name:asc' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, team => ({
            label: team.name,
            route: ['/teams', team.id],
            icon: team.icon ?? 'groups',
            color: team.color ?? null,
            imageUrl: team.imageUrl ?? null,
          }))
        )
      ),

    departments: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<AppearanceItem>(http, { basePath: '/api/v1/core/departments', sort: 'name:asc' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, department => ({
            label: department.name,
            route: ['/departments', department.id],
            icon: department.icon ?? 'apartment',
            color: department.color ?? null,
            imageUrl: department.imageUrl ?? null,
          }))
        )
      ),

    skills: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<AppearanceItem>(http, { basePath: '/api/v1/core/skills', sort: 'name:asc' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, skill => ({
            label: skill.name,
            route: ['/skills-catalog', skill.id],
            icon: skill.icon ?? 'psychology',
            color: skill.color ?? null,
            imageUrl: skill.imageUrl ?? null,
          }))
        )
      ),

    shifts: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<AppearanceItem>(http, { basePath: '/api/v1/planning/shifts', params: { type: 'definition' }, sort: 'name:asc' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, shift => ({
            label: shift.name,
            route: ['/shift-definitions', shift.id],
            icon: shift.icon ?? 'schedule',
            color: shift.color ?? null,
            imageUrl: shift.imageUrl ?? null,
          }))
        )
      ),

    plans: (_params: Record<string, string>, currentUrl: string) => {
      // Detect if we're on an edit page to preserve the edit suffix
      const isEditPage = currentUrl.endsWith('/edit');
      return fetchAllPages<NamedListItem>(http, { basePath: '/api/v1/planning/plans', sort: 'name:asc' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, plan => ({
            label: plan.name,
            route: isEditPage ? ['/planning', 'plans', plan.id, 'edit'] : ['/planning', 'plans', plan.id],
            icon: 'assignment',
          }))
        )
      );
    },

    strategies: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<AppearanceItem>(http, { basePath: '/api/v1/planning/strategies', sort: 'name:asc' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, strategy => ({
            label: strategy.name,
            route: ['/settings', 'strategies', strategy.id],
            icon: strategy.icon ?? 'lightbulb',
            color: strategy.color ?? null,
            imageUrl: strategy.imageUrl ?? null,
          }))
        )
      ),

    roles: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<{ id: string; description?: string }>(http, { basePath: '/api/v1/rbac/roles' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, role => ({
            label: role.id,
            route: ['/settings', 'roles', role.id],
            icon: 'security',
          }))
        )
      ),

    groups: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<NamedListItem>(http, { basePath: '/api/v1/rbac/groups', sort: 'name:asc' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, group => ({
            label: group.name,
            route: ['/settings', 'groups', group.id],
            icon: 'groups',
          }))
        )
      ),

    users: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<UserListItem>(http, { basePath: '/api/v1/rbac/users', sort: 'name:asc' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, user => ({
            label: user.name,
            route: ['/settings', 'users', user.id],
            icon: 'person',
            imageUrl: user.image ?? null,
          }))
        )
      ),

    locations: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<AppearanceItem>(http, { basePath: '/api/v1/core/locations', sort: 'name:asc' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, location => ({
            label: location.name,
            route: ['/locations', location.id],
            icon: location.icon ?? 'location_on',
            color: location.color ?? null,
            imageUrl: location.imageUrl ?? null,
          }))
        )
      ),

    notifications: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<{ id: string; title: string }>(http, { basePath: '/api/v1/notifications', sort: 'createdAt:desc' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, notification => ({
            label: notification.title,
            route: ['/notifications', notification.id],
            icon: 'notifications',
          }))
        )
      ),

    webhooks: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<NamedListItem>(http, { basePath: '/api/v1/notifications/webhooks', sort: 'name:asc' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, webhook => ({
            label: webhook.name,
            route: ['/settings', 'notifications', 'webhooks', webhook.id],
            icon: 'webhook',
          }))
        )
      ),

    calendars: (_params: Record<string, string>, currentUrl: string) =>
      http.get<MyCalendarsResponse>(`/api/v1/users/me/calendars`).pipe(
        map(response => {
          const mine = response;
          const ownedIds = new Set(mine.personalCalendarIds);
          const readOnlyOwnedIds = new Set(mine.readOnlyPersonalCalendarIds ?? []);

          // Extract ID from currentUrl to filter out the current calendar
          // URL pattern: .../calendars-list/UUID
          const match = currentUrl.match(/\/calendars-list\/([0-9a-f-]{36})/);
          const currentId = match ? match[1] : null;

          // Only include calendars the user can actually edit in the settings page.
          // Global calendars are always read-only; exclude them from the list.
          const isEditable = (c: Calendar) => mine.canEditAll || (ownedIds.has(c.id) && !readOnlyOwnedIds.has(c.id));
          const editablePersonal = mine.individual.filter(c => isEditable(c));
          const editableLocation = mine.factory.filter((_c: Calendar) => mine.canEditAll);

          const items: BreadcrumbItem[] = [];

          if (editablePersonal.length > 0) {
            items.push({ separator: true, sectionLabel: 'calendars.type.individual' });
            editablePersonal.slice(0, 8).forEach((c: Calendar) =>
              items.push({
                label: c.name,
                route: ['/calendars-list', c.id],
                icon: 'calendar_month',
                ...(c.id === currentId ? { isCurrent: true } : {}),
              })
            );
          }

          if (editableLocation.length > 0) {
            items.push({ separator: true, sectionLabel: 'calendars.type.factory' });
            editableLocation.slice(0, 8).forEach(c =>
              items.push({
                label: c.name,
                route: ['/calendars-list', c.id],
                icon: 'calendar_month',
                ...(c.id === currentId ? { isCurrent: true } : {}),
              })
            );
          }

          return items;
        })
      ),

    customPropertyDefinitions: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<{ id: string; label: string }>(http, { basePath: '/api/v1/core/custom-property-definitions', sort: 'label:asc' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, definition => ({
            label: definition.label,
            route: ['/settings', 'custom-properties', definition.id],
          }))
        )
      ),

    absenceTypes: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<{ id: string; label: string }>(http, {
        basePath: '/api/v1/calendar/slot-type-definitions',
        params: { category: 'absence' },
        sort: 'label:asc',
      }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, absenceType => ({
            label: absenceType.label,
            route: ['/settings', 'absences', absenceType.id],
          }))
        )
      ),

    documentTypes: (_params: Record<string, string>, currentUrl: string) =>
      http.get<{ id: string; label: string }[]>('/api/v1/documents/document-types').pipe(
        map(items =>
          toSiblingItems(items, currentUrl, documentType => ({
            label: documentType.label,
            route: ['/settings', 'document-types', documentType.id],
          }))
        )
      ),

    balanceTypes: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<{ id: string; label: string }>(http, {
        basePath: '/api/v1/time-tracking/balance-type-definitions',
        sort: 'label:asc',
      }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, balanceType => ({
            label: balanceType.label,
            route: ['/settings', 'balances', balanceType.id],
          }))
        )
      ),

    terminalGroups: (_params: Record<string, string>, currentUrl: string) =>
      http.get<{ data: NamedListItem[] }>('/api/v1/terminals/groups').pipe(
        map(({ data }) =>
          toSiblingItems(data, currentUrl, group => ({
            label: group.name,
            route: ['/settings', 'terminal-groups', group.id],
            icon: 'workspaces',
          }))
        )
      ),

    // Settings pages (static list — all settings sub-routes for breadcrumb dropdown navigation).
    // Unlike entity siblings (API-loaded), these are navigation-based (same pattern as planningChildren).
    // Each page is permission-gated: only pages the current user can access appear in the dropdown.
    settingsPages: (_params: Record<string, string>, currentUrl: string) => {
      const pages: { label: string; route: string[]; segment: string; icon: string; permission?: string; feature?: string }[] = [
        { label: 'navigation.users', route: ['/settings', 'users'], segment: 'users', icon: 'people', permission: 'settings:update' },
        { label: 'navigation.groups', route: ['/settings', 'groups'], segment: 'groups', icon: 'groups', permission: 'settings:update' },
        {
          label: 'navigation.roles',
          route: ['/settings', 'roles'],
          segment: 'roles',
          icon: 'admin_panel_settings',
          permission: 'settings:update',
        },
        {
          label: 'navigation.permissions',
          route: ['/settings', 'permissions'],
          segment: 'permissions',
          icon: 'lock',
          permission: 'settings:update',
        },
        {
          label: 'settings.auth.title',
          route: ['/settings', 'authentication'],
          segment: 'authentication',
          icon: 'lock',
          permission: 'settings:auth:read',
        },
        { label: 'settings.smtp.title', route: ['/settings', 'email'], segment: 'email', icon: 'mail', permission: 'settings:smtp:read' },
        {
          label: 'settings.ai.title',
          route: ['/settings', 'ai'],
          segment: 'ai',
          icon: 'auto-awesome',
          permission: 'settings:ai:read',
          feature: 'ai-assistant',
        },
        {
          label: 'settings.directory.title',
          route: ['/settings', 'directory'],
          segment: 'directory',
          icon: 'contacts',
          permission: 'settings:directory:read',
        },
        {
          label: 'notifications.channelConfigs.title',
          route: ['/settings', 'notifications'],
          segment: 'notifications',
          icon: 'notifications',
          permission: 'notifications:admin',
        },
        {
          label: 'settings.nav.apiKeys',
          route: ['/settings', 'api-keys'],
          segment: 'api-keys',
          icon: 'vpn_key',
          permission: 'users:profile:read',
          feature: 'public-api.api-keys',
        },
        {
          label: 'navigation.webhooks',
          route: ['/settings', 'webhooks'],
          segment: 'webhooks',
          icon: 'webhook',
          permission: 'webhooks:read',
          feature: 'public-api.webhooks',
        },
        {
          label: 'settings.customPropertyDefinitions.title',
          route: ['/settings', 'custom-properties'],
          segment: 'custom-properties',
          icon: 'tune',
          permission: 'settings:read',
        },
        {
          label: 'files.admin.title',
          route: ['/settings', 'files'],
          segment: 'files',
          icon: 'folder',
          permission: 'files:read',
        },
        {
          label: 'documents.documentTypes.title',
          route: ['/settings', 'document-types'],
          segment: 'document-types',
          icon: 'description',
          permission: 'documents:configure',
        },
        {
          label: 'navigation.ruleCatalog',
          route: ['/settings', 'rules'],
          segment: 'rules',
          icon: 'gavel',
          permission: 'settings:read',
          feature: 'timetracking',
        },
        {
          label: 'navigation.blockedWindowOverrides',
          route: ['/settings', 'blocked-windows'],
          segment: 'blocked-windows',
          icon: 'block',
          permission: 'settings:read',
          feature: 'timetracking',
        },
        {
          label: 'navigation.terminals',
          route: ['/settings', 'terminals'],
          segment: 'terminals',
          icon: 'punch_clock',
          permission: 'terminals:devices:read',
          feature: 'terminals.devices',
        },
        {
          label: 'navigation.terminalGroups',
          route: ['/settings', 'terminal-groups'],
          segment: 'terminal-groups',
          icon: 'workspaces',
          permission: 'terminals:devices:read',
          feature: 'terminals.devices',
        },
        {
          label: 'navigation.badgeLookup',
          route: ['/settings', 'badge-lookup'],
          segment: 'badge-lookup',
          icon: 'search',
          permission: 'terminals:badges:read',
          feature: 'terminals.devices',
        },
        {
          label: 'navigation.absenceTypes',
          route: ['/settings', 'absences'],
          segment: 'absences',
          icon: 'event_busy',
          permission: 'settings:read',
          feature: 'timetracking',
        },
        {
          label: 'navigation.balanceAccounts',
          route: ['/settings', 'balances'],
          segment: 'balances',
          icon: 'account_balance',
          permission: 'settings:read',
          feature: 'timetracking',
        },
        {
          label: 'settings.invoicing.title',
          route: ['/settings', 'invoicing'],
          segment: 'invoicing',
          icon: 'receipt_long',
          permission: 'projects:read',
        },
        {
          label: 'strategies.title',
          route: ['/settings', 'strategies'],
          segment: 'strategies',
          icon: 'lightbulb',
          permission: 'strategies:read',
        },
      ];

      // Extract the settings sub-segment from currentUrl (e.g. '/settings/email' → 'email')
      const urlSegments = currentUrl.replace(/^\//, '').split('/');
      const currentSegment = urlSegments[1] ?? '';

      const siblings: BreadcrumbItem[] = pages
        .filter(p => !p.permission || permissionService.hasPermission(p.permission))
        .filter(p => !p.feature || featureAccess.hasFeature(p.feature))
        .map(p => {
          const item: BreadcrumbItem = { label: p.label, icon: p.icon, route: p.route };
          return p.segment === currentSegment ? { ...item, isCurrent: true } : item;
        });

      return of(siblings);
    },

    // Navigation-based siblings (derived from NAV_CONFIG — Planning section items)
    planningChildren: (_params: Record<string, string>, currentUrl: string) => {
      // Find the Planning section in the nav config
      const planningSection = NAV_CONFIG.find(entry => isNavSection(entry) && entry.label === 'navigation.sectionPlanning');
      if (!planningSection || !isNavSection(planningSection)) {
        return of([]);
      }

      const siblings: BreadcrumbItem[] = planningSection.items
        .filter((item): item is typeof item & { route: string } => typeof item.route === 'string')
        .map(item => {
          const route: string[] = ['/', ...item.route.split('/').filter(Boolean)];
          const sibling: BreadcrumbItem = { label: item.label, icon: item.icon, route };
          return normalizeUrl(item.route) === normalizeUrl(currentUrl) ? { ...sibling, isCurrent: true } : sibling;
        });

      return of(siblings);
    },
  };
}

/**
 * Provides breadcrumb resolvers and loaders for the application.
 * Add this to your app.config.ts providers array.
 */
export function provideBreadcrumbConfig(): Provider[] {
  return [
    {
      provide: BREADCRUMB_LABEL_RESOLVERS,
      useFactory: createLabelResolvers,
      deps: [HttpClient],
    },
    {
      provide: BREADCRUMB_SIBLINGS_LOADERS,
      useFactory: createSiblingsLoaders,
      deps: [HttpClient, PermissionService, FeatureAccessClientService],
    },
  ];
}
